import isEqual from 'lodash/isEqual';
import { mapActions, mapGetters } from 'vuex';

import DataInputService from './DataInputService';
import {
	EXTERNAL_SOURCE_TAB_KEY,
	PREVIOUS_STEP_TABLE_OUTPUT,
	REFRESH_DATA,
	SELECT_TAB_KEY,
	SET_TEXT_VALUE,
	SET_VALUE_FROM_PREVIOUS_STEP,
	TABLE_ONLY_ALLOWED_VALUE_TYPES,
	UPDATE_SEARCH_STRING,
} from './data-input-menu.const';
import { markString } from './helpers';
import DataInputMenuService from '../data-input-menu-service';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { getAction } from '@/store/modules/actions/getters';
import { getService, servicesActionsIds, getServiceBySlug } from '@/store/modules/apps/getters';
import { isConfigurationsOn } from '@/store/modules/featureFlags/getters';
import { getNode, getPreviousStepDataForNode, getGlobalWorkflowData } from '@/store/modules/nodes/getters';
import { getPlacement } from '@/store/modules/placements/getters';
import { editNode } from '@/store/modules/selectedNode/getters';
import {
	editWorkflow,
	selectedWorkflowId,
	processWorkflows,
	getNodeByUuidForSelectedWorkflowVersion,
} from '@/store/modules/selectedWorkflow/getters';
import { useNodeAncestorIds, useBranchConsolidatePathAncestorIds } from '@/store/modules/workflowGraphs/getters';
import {
	getWorkflowDraftVersion,
	element,
	elementContextualData,
	rootNode,
	generalSetupMappedFields,
	userSetupMappedFields,
	nodesDataSorted,
} from '@/store/modules/workflows/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import ParamsService from '@/utils/ParamsService';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { isNullOrUndefined } from '@/utils/helpers';
import { TEST_RESULT_STATUSES, PLY_ICON_SERVICE_SLUG } from '@/utils/workflow-node.const';
export default {
	name: 'ExtendedDataInputMenuMixin',
	props: {
		nodeUUID: { type: String },
		tabItems: { type: Array },
		fieldParams: { type: [Array, Object] },
		param: { type: Object },
		tab: { type: Number },
		curTabCode: { type: String },
		branchID: { type: [String, Number] },
		// The following is to allow an array selection from dynamic source
		allowMultipleSelection: { type: Boolean, default: false },
		localValue: { type: [String, Array, Object, Number] },
		// the following is to manage which value types are valid to choose
		valuesTypes: { type: Array, validator: DataInputMenuService.valueTypesValidator, default: () => [] },
	},
	computed: {
		...mapGetters([
			editWorkflow.fullPath,
			selectedWorkflowId.fullPath,
			getService.fullPath,
			servicesActionsIds.fullPath,
			getPreviousStepDataForNode.fullPath,
			getNodeByUuidForSelectedWorkflowVersion.fullPath,
			getNode.fullPath,
			editNode.fullPath,
			element.fullPath,
			getAction.fullPath,
			rootNode.fullPath,
			generalSetupMappedFields.fullPath,
			userSetupMappedFields.fullPath,
			isConfigurationsOn.fullPath,
			processWorkflows.fullPath,
			getWorkflowDraftVersion.fullPath,
			elementContextualData.fullPath,
			getServiceBySlug.fullPath,
			nodesDataSorted.fullPath,
		]),
		...mapGettersShorthand({
			getGlobalWorkflowData,
			getPlacement,
			useNodeAncestorIds,
			useBranchConsolidatePathAncestorIds,
		}),
		processWorkflowItems() {
			return this[processWorkflows.fullPath].map((wf) => ({
				nodeInfo: {
					nodeTitle: wf.workflow_title,
					serviceSlug: PLY_ICON_SERVICE_SLUG,
					isMultiple: false,
					testResultStatus: TEST_RESULT_STATUSES.SUCCESS,
				},
				key: wf.id,
				itemKey: wf.id,
				active: false,
			}));
		},
		wfId() {
			return this.selectedProcessWorkflowId || this[selectedWorkflowId.fullPath];
		},
		dataSourcesItems() {
			// Important:
			// This doesn't have to have a wfId as we are using this component in creation as well for conditional rendering fields!
			if (!this.curNode) {
				return [];
			}

			const workflow = this[editWorkflow.fullPath];
			const appInfoGetter = this[getService.fullPath];
			const nodeDataGetter = this[getNode.fullPath];
			const appActionsIdsGetter = this[servicesActionsIds.fullPath];
			const actionInfoGetter = this[getAction.fullPath];
			const getPlacementGetter = this.getPlacement;
			const elementContextualDataGetter = this[elementContextualData.fullPath](this.wfId);
			const getPreviousStepDataForNodeGetter = this.getPreviousStepDataForNode;
			const getGlobalWorkflowRunData = this.getGlobalWorkflowData(this.wfId, false);

			const currentElement = this[element.fullPath](this.wfId);

			const generalSetupFields = this[generalSetupMappedFields.fullPath](this.wfId);
			const userSetupFields = this[userSetupMappedFields.fullPath](this.wfId);

			const sortedNodeIds = this.wfId ? (this[nodesDataSorted.fullPath](this.wfId) || []).map(({ id }) => id) : [];

			const nodeIds = this.selectedProcessWorkflowId ? sortedNodeIds : [...(this.parentNodeIds || [])];

			return DataInputService.getDataSourcesItems(
				nodeIds,
				appInfoGetter,
				nodeDataGetter,
				appActionsIdsGetter,
				actionInfoGetter,
				getPlacementGetter,
				getGlobalWorkflowRunData,
				elementContextualDataGetter,
				getPreviousStepDataForNodeGetter,
				this.isMultipleOnly,
				currentElement,
				this.searchString,
				this.itemsCollapsedStatus,
				generalSetupFields,
				userSetupFields,
				this[isConfigurationsOn.fullPath],
				workflow,
				this.curNode
			);
		},
		localTab: {
			get() {
				return this.tab;
			},
			set(value) {
				this.$emit('update:tab', value);
			},
		},
		curNode() {
			return this.nodeUUID ? this[getNodeByUuidForSelectedWorkflowVersion.fullPath](this.nodeUUID) : this[editNode.fullPath];
		},
		isCurTabFieldParams() {
			return this.curTabCode === EXTERNAL_SOURCE_TAB_KEY;
		},
		isCurTabSelectParams() {
			return this.curTabCode === SELECT_TAB_KEY;
		},
		isCurTabTableParams() {
			return this.curTabCode === PREVIOUS_STEP_TABLE_OUTPUT;
		},
		items() {
			return this.dataSourcesItems.filter((dsi) => dsi.key === this.tableNodeId);
		},
		parentNodeIds() {
			if (!this.curNode || !this.curNode.workflow) {
				// We look for workflow as its indicates this is part of a workflow and not in some hack like conditional rendering
				return [];
			}
			const isBranchConsolidate = WorkflowNodeService.isConsolidateDataNodeAndAction(
				this.curNode,
				this[getAction.fullPath](this.curNode.action)
			);
			if (isBranchConsolidate) {
				return [
					...this.useNodeAncestorIds({ node: this.curNode }),
					...this.useBranchConsolidatePathAncestorIds({ node: this.curNode, pathId: this.branchID }),
				];
			}
			return this.useNodeAncestorIds({ node: this.curNode });
		},
		getPreviousStepDataForNode() {
			return this[getPreviousStepDataForNode.fullPath];
		},
		menuMessage() {
			return DataInputMenuService.getDataInputMenuMessageForValueTypes(this.valuesTypes);
		},
		isMultipleOnly() {
			return isEqual(this.valuesTypes, TABLE_ONLY_ALLOWED_VALUE_TYPES);
		},
	},
	methods: {
		...mapActions(['nodes/getNodeData']),
		closeMenu() {
			this.$emit('close-menu');
		},
		markSearchString(str) {
			return markString(this.searchString, str);
		},
		setTextItem(text) {
			this.$emit(SET_TEXT_VALUE, text);
			this.closeMenu();
		},
		async setItemFromInputMenu(app, valueKey, formulaName, formulaParams) {
			let localItemKey = valueKey;
			if (this.isDynamicDataMapperValue(app) && this.needsToBeMapped(app, valueKey)) {
				localItemKey = this.getCorrectPath(app, valueKey);
				await this.openAddMappingToMapperDialog(app, valueKey);
			}

			const valueToSet = ParamsService.buildKeyForPath(
				this[rootNode.fullPath](this.wfId),
				app.key,
				localItemKey,
				formulaName,
				formulaParams
			);
			this.$emit(SET_VALUE_FROM_PREVIOUS_STEP, valueToSet);
			this.closeMenu();
		},
		refreshFieldParams() {
			this.$emit(REFRESH_DATA);
		},
		setCollapsedItem(itemKey, isCollapsed) {
			this.$set(this.itemsCollapsedStatus, itemKey, isCollapsed);
		},
		getFlowParams() {
			if (!this.curNode) {
				return;
			}
			this.parentNodeIds.forEach((nodeId) => {
				const previousStepData = this.getPreviousStepDataForNode;
				if (!nodeId || isNullOrUndefined(previousStepData)) {
					return;
				}
				this['nodes/getNodeData']({ id: nodeId, forceGet: false });
			});
		},
		getCorrectPath(item, itemKey) {
			// for insert data that is mapped from 'has_user_schema' typed actions
			// set path including mapped source field uuid so we can replace it later on in the server.
			return this.isDynamicDataMapperValue(item) ? `${item.nodeInfo.userSchemaMappingsSourceFieldUuid}.${itemKey}` : itemKey;
		},
		isDynamicDataMapperValue(item) {
			return item?.nodeInfo?.userSchemaMappingsSourceFieldUuid && item?.nodeInfo?.hasUserSchema;
		},
		needsToBeMapped(item, itemKey) {
			return !item?.nodeInfo?.skipMappingKeys.includes(itemKey);
		},
		async openAddMappingToMapperDialog(item, itemKey) {
			const itemKeyToMap = itemKey.split('.')[0]; // we map only the first level of the itemKey
			// check if this is needed for mapping and if first of its mapping and if so - open dialog for mapping item
			const data = this.widgetFieldData(item.nodeInfo.userSchemaMappingsSourceFieldUuid);
			const mapping = data.widgetField?.metadata?.source?.mappings.find((m) => m.originalKey === itemKeyToMap);
			if (!mapping) {
				await AppDialogManagerService.openAddMappingToMapperDialog({
					originalKey: itemKeyToMap,
					widgetFieldUUID: item.nodeInfo.userSchemaMappingsSourceFieldUuid,
					service: this[getServiceBySlug.fullPath](item?.nodeInfo?.serviceSlug),
				});
			}
		},
	},
	data() {
		return {
			searchString: '',
			itemsCollapsedStatus: {},
		};
	},
	watch: {
		searchString: {
			handler(newV) {
				this.$emit(UPDATE_SEARCH_STRING, newV);
			},
		},
	},
	mounted() {
		this.getFlowParams();
	},
};
